/**
 * OpenRouter Proxy Provider
 * Uses the existing OpenRouter proxy endpoints
 */

import { BaseProvider } from '../base/BaseProvider'
import type { AIResponseWithReason, ContinueContext } from '../../types'
import { TokenManager, getDeviceId } from '../../utils/validation'

export class OpenRouterProvider extends BaseProvider {
  name = 'OpenRouter'
  type: 'direct' | 'proxy' = 'proxy'
  
  private proxyUrl: string
  private model: string
  private authToken: string = ''
  private deviceId: string
  private temperature: number = 0.3
  private maxTokens: number = 8192  // Most OpenRouter models have 4K-8K output limits
  
  constructor(proxyUrl: string, model = 'openai/gpt-3.5-turbo') {
    super()
    this.proxyUrl = proxyUrl
    this.model = model
    this.deviceId = getDeviceId()
    
    // Check for cached token first
    const cachedToken = TokenManager.get()
    if (cachedToken) {
      this.authToken = cachedToken
    }
  }
  
  private async requestAuthToken(): Promise<void> {
    const isDev = import.meta.env.DEV
    const authUrl = isDev 
      ? '/api/auth/token'  // Vite will proxy this
      : `${this.proxyUrl}/api/auth/token`
    
    try {
      const response = await fetch(authUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Device-Id': this.deviceId
        },
        body: JSON.stringify({
          deviceId: this.deviceId
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        this.authToken = data.token
        TokenManager.set(data.token)
      } else {
        // Use cached token if available
        const cachedToken = TokenManager.get()
        if (cachedToken) {
          this.authToken = cachedToken
        }
      }
    } catch (error) {
      console.error('Failed to get auth token:', error)
      // Use cached token if available
      const cachedToken = TokenManager.get()
      if (cachedToken) {
        this.authToken = cachedToken
      }
    }
  }
  
  private async getAuthHeaders(): Promise<HeadersInit> {
    // Ensure we have a valid token
    if (!this.authToken) {
      await this.requestAuthToken()
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.authToken}`,
      'X-Device-Id': this.deviceId
    }
  }
  
  private async callProxy(systemPrompt: string, userPrompt: string, originalUserRequest?: string): Promise<AIResponseWithReason> {
    let headers = await this.getAuthHeaders()
    
    // Convert to OpenAI message format for proxy
    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ]
    
    
    let response = await fetch(`${this.proxyUrl}/api/openrouter/proxy`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model: this.model,
        messages,
        provider: {
          sort: 'throughput'
        }
      })
    })
    
    // Handle 401 - refresh token and retry once
    if (response.status === 401) {
      this.authToken = ''
      TokenManager.remove()
      await this.requestAuthToken()
      
      if (this.authToken) {
        headers = await this.getAuthHeaders()
        response = await fetch(`${this.proxyUrl}/api/openrouter/proxy`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            model: this.model,
            messages,
            provider: {
              sort: 'throughput'
            }
          })
        })
      }
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }))
      throw new Error(error.message || `OpenRouter proxy error: ${response.status}`)
    }
    
    const data = await response.json()
    
    
    // Handle OpenAI format response from proxy
    if (data.choices && data.choices[0]) {
      const choice = data.choices[0]
      let content = choice.message?.content || choice.text
      
      // Handle reasoning models (like ultrathink) that may have empty content but reasoning field
      if (!content && choice.message?.reasoning) {
        // For reasoning models, the actual response might be in the reasoning field
        // or we need to extract it from structured output
        const reasoning = choice.message.reasoning
        
        // Check if reasoning contains the actual response text
        if (reasoning && typeof reasoning === 'string') {
          // Try to extract the modified/generated text from reasoning
          // Look for patterns like "수정된 텍스트:", "결과:", etc.
          const patterns = [
            /수정된 텍스트[:：]\s*(.+)/s,
            /결과[:：]\s*(.+)/s,
            /최종[:：]\s*(.+)/s,
            /답변[:：]\s*(.+)/s,
            /생성된 텍스트[:：]\s*(.+)/s
          ]
          
          for (const pattern of patterns) {
            const match = reasoning.match(pattern)
            if (match && match[1]) {
              content = match[1].trim()
              
              // Clean up content - remove any request echoes
              if (originalUserRequest && content.includes(originalUserRequest)) {
                // Remove the user prompt if it appears in the content
                content = content.replace(originalUserRequest, '').trim()
                // Also remove common patterns around prompts
                content = content
                  .replace(/^["']/, '') // Remove leading quotes
                  .replace(/["']$/, '') // Remove trailing quotes
                  .replace(/^.*?[:：]\s*/, '') // Remove any prefix ending with colon
                  .trim()
              }
              
              // Remove common prompt patterns
              content = content
                .replace(/^원문[:：]\s*.*?\n/gm, '')
                .replace(/^요청사항[:：]\s*.*?\n/gm, '')
                .replace(/^요청[:：]\s*.*?\n/gm, '')
                .trim()
              
              break
            }
          }
          
          // If no pattern matched, use the entire reasoning as content but clean it
          if (!content) {
            content = reasoning
            
            // Try to find the actual response part if reasoning contains request echo
            if (originalUserRequest && content.includes(originalUserRequest)) {
              const promptIndex = content.lastIndexOf(originalUserRequest)
              const afterPrompt = content.substring(promptIndex + originalUserRequest.length).trim()
              if (afterPrompt) {
                content = afterPrompt
              }
            }
          }
        }
      }
      
      // Check for tool_calls (some models use this format)
      if (!content && choice.message?.tool_calls) {
        const toolCall = choice.message.tool_calls[0]
        if (toolCall?.function?.arguments) {
          try {
            const args = JSON.parse(toolCall.function.arguments)
            content = args.text || args.result || JSON.stringify(args)
          } catch (e) {
            content = toolCall.function.arguments
          }
        }
      }
      
      // Validate we have actual content
      if (!content || content.trim() === '') {
        console.error('[OpenRouter] Empty response from proxy:', data)
        throw new Error('AI 서버에서 유효한 응답을 받지 못했습니다.')
      }
      
      
      return {
        text: content,
        reason: '요청을 성공적으로 처리했습니다'
      }
    }
    
    // Fallback to old format if present
    const fallbackText = data.result || data.text || ''
    
    // Validate fallback text
    if (!fallbackText || fallbackText.trim() === '') {
      console.error('[OpenRouter] Empty response from proxy (fallback):', data)
      throw new Error('AI 서버에서 유효한 응답을 받지 못했습니다.')
    }
    
    return {
      text: fallbackText,
      reason: data.reason || '요청을 처리했습니다'
    }
  }
  
  // OpenRouter uses proxy endpoints, so we don't use the base callAPI method
  protected async callAPI(systemPrompt: string, userPrompt: string): Promise<string> {
    throw new Error('OpenRouter should not use callAPI directly')
  }
  
  // Override each method to use streaming proxy endpoints
  async paraphrase(text: string, options?: any): Promise<AIResponseWithReason> {
    return this.callStreamingOperation('paraphrase', text)
  }
  
  async expand(text: string): Promise<AIResponseWithReason> {
    return this.callStreamingOperation('expand', text)
  }
  
  async shrink(text: string): Promise<AIResponseWithReason> {
    return this.callStreamingOperation('shrink', text)
  }
  
  // Streaming versions that use callStreamingOperation for reliability
  async paraphraseStreaming(text: string, onChunk?: (chunk: string) => void): Promise<AIResponseWithReason> {
    return this.callStreamingOperation('paraphrase', text, onChunk)
  }
  
  async expandStreaming(text: string, onChunk?: (chunk: string) => void): Promise<AIResponseWithReason> {
    return this.callStreamingOperation('expand', text, onChunk)
  }
  
  async shrinkStreaming(text: string, onChunk?: (chunk: string) => void): Promise<AIResponseWithReason> {
    return this.callStreamingOperation('shrink', text, onChunk)
  }
  
  // Helper method for streaming operations
  private async callStreamingOperation(
    operation: 'paraphrase' | 'expand' | 'shrink',
    text: string,
    onChunk?: (chunk: string) => void
  ): Promise<AIResponseWithReason> {
    const headers = await this.getAuthHeaders()
    
    
    const response = await fetch(`${this.proxyUrl}/api/openrouter/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text,
        operation,
        model: this.model,
        provider: {
          sort: 'throughput'
        }
      })
    })
    
    if (response.status === 401) {
      this.authToken = ''
      TokenManager.remove()
      await this.requestAuthToken()
      
      // Retry with new token
      const newHeaders = await this.getAuthHeaders()
      const retryResponse = await fetch(`${this.proxyUrl}/api/openrouter/stream`, {
        method: 'POST',
        headers: newHeaders,
        body: JSON.stringify({
          text,
          operation,
          model: this.model,
          provider: {
            sort: 'throughput'
          }
        })
      })
      
      if (!retryResponse.ok) {
        const errorText = await retryResponse.text()
        throw new Error(`OpenRouter proxy error: ${retryResponse.status} - ${errorText}`)
      }
      
      return this.processOperationStreamResponse(retryResponse, onChunk)
    }
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`OpenRouter proxy error: ${response.status} - ${errorText}`)
    }
    
    return this.processOperationStreamResponse(response, onChunk)
  }
  
  private async processOperationStreamResponse(
    response: Response,
    onChunk?: (chunk: string) => void
  ): Promise<AIResponseWithReason> {
    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }
    
    const decoder = new TextDecoder()
    let fullText = ''
    let reason = ''
    let rawChunks: string[] = [] // For debugging
    
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        
        const chunk = decoder.decode(value)
        rawChunks.push(chunk) // Store for debugging
        const lines = chunk.split('\n').filter(line => line.trim() !== '')
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            
            if (data === '[DONE]') {
              continue
            }
            
            try {
              const parsed = JSON.parse(data)
              
              // Skip metadata chunks - don't add to text
              if (parsed.metadata) {
                if (parsed.metadata.reason) {
                  reason = parsed.metadata.reason
                }
                // Don't process this chunk as text
                continue
              }
              
              if (parsed.text) {
                fullText += parsed.text
                if (onChunk) {
                  onChunk(parsed.text)
                }
              }
              
              // Check for error in response
              if (parsed.error) {
                console.error('[OpenRouter] Server returned error:', parsed.error)
                throw new Error(parsed.error.message || 'Server error')
              }
            } catch (e) {
              console.warn('[OpenRouter] Failed to parse chunk:', data, e)
              continue
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
    
    
    // Check if we got any response
    if (!fullText || fullText.trim() === '') {
      console.error('[OpenRouter] No response received from streaming operation. Raw chunks:', rawChunks)
      throw new Error('AI 서버에서 응답을 받지 못했습니다. 다시 시도해주세요.')
    }
    
    return {
      text: fullText.trim(),
      reason: reason || '처리가 완료되었습니다'
    }
  }
  
  async summarize(text: string): Promise<string> {
    const systemPrompt = '한국어 텍스트를 핵심 내용 위주로 요약해주세요.'
    const response = await this.callProxy(systemPrompt, text)
    return response.text
  }
  
  // Add processCustomPrompt method
  async processCustomPrompt(text: string, userPrompt: string, mode?: 'sentence' | 'paragraph'): Promise<AIResponseWithReason> {
    // Check if this is a generation request (empty text) or modification request
    const isGeneration = !text || text.trim() === ''
    
    let systemPrompt: string
    let fullUserPrompt: string
    
    if (isGeneration) {
      // Text generation mode
      systemPrompt = `한국어 텍스트를 생성하는 전문 작가입니다.`
      
      if (mode === 'sentence') {
        systemPrompt += ' 한 문장으로 간결하게 작성하세요.'
      } else if (mode === 'paragraph') {
        systemPrompt += ' 3-5 문장으로 구성된 한 문단으로 작성하세요.'
      }
      
      systemPrompt += ' 생성된 텍스트만 반환하세요.'
      fullUserPrompt = `요청사항: ${userPrompt}`
    } else {
      // Text modification mode
      systemPrompt = `다음 텍스트를 사용자의 요청에 따라 수정해주세요.`
      
      if (mode === 'sentence') {
        systemPrompt += ' 한 문장으로 간결하게 작성하세요.'
      } else if (mode === 'paragraph') {
        systemPrompt += ' 한 문단으로 자세하게 작성하세요.'
      }
      
      systemPrompt += ' 수정된 텍스트만 반환하세요.'
      fullUserPrompt = `원문: ${text}\n\n요청사항: ${userPrompt}`
    }
    
    
    const result = await this.callProxy(systemPrompt, fullUserPrompt, userPrompt)
    
    // Only provide reason for modifications, not for generations
    if (isGeneration) {
      return {
        text: result.text,
        reason: undefined
      }
    }
    
    return {
      text: result.text,
      reason: `프롬프트: "${userPrompt}"`
    }
  }
  
  /**
   * Process custom prompt with real streaming support
   */
  async processCustomPromptStreaming(
    text: string, 
    userPrompt: string, 
    mode?: 'sentence' | 'paragraph',
    onChunk?: (chunk: string) => void
  ): Promise<AIResponseWithReason> {
    // Check if this is a generation request (empty text) or modification request
    const isGeneration = !text || text.trim() === ''
    
    const headers = await this.getAuthHeaders()
    
    
    const response = await fetch(`${this.proxyUrl}/api/openrouter/custom`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text: text || '',
        userPrompt,
        mode,
        model: this.model,
        provider: {
          sort: 'throughput'
        }
      })
    })
    
    if (response.status === 401) {
      this.authToken = ''
      TokenManager.remove()
      await this.requestAuthToken()
      
      // Retry with new token
      const newHeaders = await this.getAuthHeaders()
      const retryResponse = await fetch(`${this.proxyUrl}/api/openrouter/custom`, {
        method: 'POST',
        headers: {
          ...newHeaders,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify({
          text: text || '',
          userPrompt,
          mode,
          model: this.model,
          provider: {
            sort: 'throughput'
          }
        }),
        // Avoid QUIC issues
        mode: 'cors',
        credentials: 'include'
      })
      
      if (!retryResponse.ok) {
        const errorText = await retryResponse.text()
        throw new Error(`OpenRouter proxy error: ${retryResponse.status} - ${errorText}`)
      }
      
      return this.processCustomStreamResponse(retryResponse, onChunk, isGeneration, userPrompt)
    }
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`OpenRouter proxy error: ${response.status} - ${errorText}`)
    }
    
    return this.processCustomStreamResponse(response, onChunk, isGeneration, userPrompt)
  }
  
  private async processCustomStreamResponse(
    response: Response,
    onChunk?: (chunk: string) => void,
    isGeneration?: boolean,
    userPrompt?: string
  ): Promise<AIResponseWithReason> {
    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }
    
    const decoder = new TextDecoder()
    let fullText = ''
    let reason = ''
    let reasoningText = ''
    
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        
        const chunk = decoder.decode(value)
        const lines = chunk.split('\n').filter(line => line.trim() !== '')
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            
            if (data === '[DONE]') {
              continue
            }
            
            try {
              const parsed = JSON.parse(data)
              
              
              // Skip metadata chunks - don't add to text
              if (parsed.metadata) {
                if (parsed.metadata.reason) {
                  reason = parsed.metadata.reason
                }
                // Don't process this chunk as text
                continue
              }
              
              // Handle standard text chunks
              if (parsed.text) {
                fullText += parsed.text
                if (onChunk) {
                  onChunk(parsed.text)
                }
              }
              
              // Handle OpenAI-style choices format
              if (parsed.choices && parsed.choices[0]) {
                const choice = parsed.choices[0]
                const delta = choice.delta || choice.message
                
                if (delta?.content) {
                  fullText += delta.content
                  if (onChunk) {
                    onChunk(delta.content)
                  }
                }
                
                // Collect reasoning text for reasoning models
                if (delta?.reasoning) {
                  reasoningText += delta.reasoning
                }
              }
            } catch (e) {
              // Skip non-JSON lines
              continue
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
    
    // If we got reasoning but no content (reasoning models), extract text from reasoning
    if (!fullText && reasoningText) {
      // Try to extract the actual response from reasoning
      const patterns = [
        /수정된 텍스트[:：]\s*(.+)/s,
        /결과[:：]\s*(.+)/s,
        /최종[:：]\s*(.+)/s,
        /답변[:：]\s*(.+)/s,
        /생성된 텍스트[:：]\s*(.+)/s
      ]
      
      for (const pattern of patterns) {
        const match = reasoningText.match(pattern)
        if (match && match[1]) {
          fullText = match[1].trim()
          
          // Clean up fullText - remove any request echoes
          if (userPrompt && fullText.includes(userPrompt)) {
            // Remove the user prompt if it appears in the content
            fullText = fullText.replace(userPrompt, '').trim()
            // Also remove common patterns around prompts
            fullText = fullText
              .replace(/^["']/, '') // Remove leading quotes
              .replace(/["']$/, '') // Remove trailing quotes
              .replace(/^.*?[:：]\s*/, '') // Remove any prefix ending with colon
              .trim()
          }
          
          // Remove common prompt patterns
          fullText = fullText
            .replace(/^원문[:：]\s*.*?\n/gm, '')
            .replace(/^요청사항[:：]\s*.*?\n/gm, '')
            .replace(/^요청[:：]\s*.*?\n/gm, '')
            .trim()
          
          break
        }
      }
      
      // If no pattern matched, use the entire reasoning as content but clean it
      if (!fullText) {
        fullText = reasoningText
        
        // Try to find the actual response part if reasoning contains request echo
        if (userPrompt && fullText.includes(userPrompt)) {
          const promptIndex = fullText.lastIndexOf(userPrompt)
          const afterPrompt = fullText.substring(promptIndex + userPrompt.length).trim()
          if (afterPrompt) {
            fullText = afterPrompt
          }
        }
      }
    }
    
    
    // Check if we got any response
    if (!fullText || fullText.trim() === '') {
      console.error('[OpenRouter] No response received from custom prompt streaming')
      throw new Error('AI 서버에서 응답을 받지 못했습니다. 다시 시도해주세요.')
    }
    
    // Return response with proper reason handling
    return {
      text: fullText.trim(),
      reason: reason || (isGeneration ? undefined : `프롬프트: "${userPrompt}"`)
    }
  }
  
  private async processStreamingResponse(
    response: Response,
    onChunk?: (chunk: string) => void,
    isGeneration?: boolean,
    userPrompt?: string
  ): Promise<AIResponseWithReason> {
    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }
    
    const decoder = new TextDecoder()
    let fullText = ''
    
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        
        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            
            if (data === '[DONE]') {
              continue
            }
            
            try {
              const parsed = JSON.parse(data)
              
              if (parsed.content) {
                fullText += parsed.content
                
                // Call the chunk callback if provided
                if (onChunk) {
                  onChunk(parsed.content)
                }
              }
            } catch (e) {
              // Skip non-JSON lines
              continue
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
    
    // Clean up the result
    fullText = fullText.trim()
    
    // Only provide reason for modifications, not for generations
    if (isGeneration) {
      return {
        text: fullText,
        reason: undefined
      }
    }
    
    return {
      text: fullText,
      reason: userPrompt ? `프롬프트: "${userPrompt}"` : undefined
    }
  }
  
  /**
   * Continue writing with streaming support through proxy
   */
  async continueStreaming(
    text: string,
    context?: ContinueContext,
    onChunk?: (chunk: string) => void
  ): Promise<string | AIResponseWithReason> {
    const headers = await this.getAuthHeaders()

    // Debug: Log request
    const requestBody = {
      text,
      context,
      mode: context?.mode || 'sentence',
      cursorPosition: context?.cursorPosition,
      model: this.model,
      stream: true
    }

    const response = await fetch(`${this.proxyUrl}/api/openrouter/continue-stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...requestBody,
        provider: {
          sort: 'throughput'
        }
      })
    })
    
    if (response.status === 401) {
      this.authToken = ''
      TokenManager.remove()
      await this.requestAuthToken()
      
      // Retry with new token
      const newHeaders = await this.getAuthHeaders()
      const retryResponse = await fetch(`${this.proxyUrl}/api/openrouter/continue-stream`, {
        method: 'POST',
        headers: newHeaders,
        body: JSON.stringify({
          text,
          context,
          mode: context?.mode || 'sentence',
          cursorPosition: context?.cursorPosition,
          model: this.model,
          stream: true,
          provider: {
            sort: 'throughput'
          }
        })
      })
      
      if (!retryResponse.ok) {
        throw new Error(`OpenRouter proxy error: ${retryResponse.status}`)
      }
      
      return this.processStreamResponse(retryResponse, onChunk)
    }
    
    if (!response.ok) {
      throw new Error(`OpenRouter proxy error: ${response.status}`)
    }
    
    return this.processStreamResponse(response, onChunk)
  }

  private async processStreamResponse(
    response: Response,
    onChunk?: (chunk: string) => void
  ): Promise<string | AIResponseWithReason> {
    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }

    const decoder = new TextDecoder()
    let fullText = ''
    let chunkCount = 0

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n').filter(line => line.trim() !== '')

        for (const line of lines) {
          // Server-sent events format from proxy
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              break
            }

            try {
              const parsed = JSON.parse(data)
              // Handle both direct text and delta format
              const content = parsed.text || parsed.delta?.text || parsed.choices?.[0]?.delta?.content
              if (content) {
                chunkCount++
                fullText += content
                onChunk?.(content)
              }
            } catch (e) {
              // Skip failed chunk parsing
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    // Check if we got any response
    if (!fullText || fullText.trim() === '') {
      throw new Error('AI 서버에서 응답을 받지 못했습니다. 다시 시도해주세요.')
    }

    return {
      text: fullText,
      reason: undefined  // Continue is generation, not modification
    }
  }

  async continue(text: string, context?: ContinueContext): Promise<string | AIResponseWithReason> {
    const headers = await this.getAuthHeaders()

    const response = await fetch(`${this.proxyUrl}/api/openrouter/continue`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text,
        context,
        mode: context?.mode || 'sentence',
        cursorPosition: context?.cursorPosition,
        model: this.model,
        provider: {
          sort: 'throughput'
        }
      })
    })
    
    if (response.status === 401) {
      this.authToken = ''
      TokenManager.remove()
      await this.requestAuthToken()
      
      if (this.authToken) {
        const retryHeaders = await this.getAuthHeaders()
        const retryResponse = await fetch(`${this.proxyUrl}/api/openrouter/continue`, {
          method: 'POST',
          headers: retryHeaders,
          body: JSON.stringify({
            text,
            context,
            mode: context?.mode || 'sentence',
            cursorPosition: context?.cursorPosition,
            model: this.model,
            provider: {
              sort: 'throughput'
            }
          })
        })
        
        if (!retryResponse.ok) {
          throw new Error(`API error: ${retryResponse.status}`)
        }
        
        const retryData = await retryResponse.json()
        // Continue is generation, not modification - don't show reason
        return {
          text: retryData.result || retryData.continuation,
          reason: undefined
        }
      }
    }
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`)
    }
    
    const data = await response.json()
    // Continue is generation, not modification - don't show reason
    return {
      text: data.result || data.continuation,
      reason: undefined
    }
  }
  
  async critique(text: string): Promise<string> {
    // Critique endpoint not yet implemented on server
    throw new Error('Critique feature not yet available')
  }
}